// CheckoutOneShot.jsx — Checkout dedicado de la tienda de sueltos (OneShot / LimpackFlex).
// AISLADO: no toca CheckoutFlow.jsx (kits/suscripción quedan intactos).
// UX idéntico al checkout de producción: reusa las clases .cf-* (copiadas de CheckoutFlow) +
// el wrapper .tv5-page-checkout + CHECKOUT_CSS (tema oscuro v5).
// Envío por la DIRECCIÓN del cliente (point-in-polygon 63 barrios) → LimpackFlex por zona
// (hoy/mañana, corte 16h) · Canelones (Costa de Oro + barrios privados) → extendida por lista ·
// Interior (otros deptos) → despacho a Tres Cruces con localidad + CI.
// Fuente de verdad del envío: RPC calcular_envio_oneshot / crear_pedido_oneshot.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { useCart } from '../../lib/CartContext'
import { geocodificar, barrioOficialDeCoords, buscarDirecciones } from '../../lib/geo'
import { calcularEnvioOneshot } from '../../lib/envios'
import { crearPedidoOneshot, getProductosTienda } from '../../lib/portalClienteApi'
import { initMpDeviceId, getMpDeviceId } from '../../lib/mpDeviceId'
import { getUTM, getRef } from '../../lib/utm'
import TurnstileWidget from '../../components/TurnstileWidget'
import MapPin from '../../components/MapPin'
import { TopNav, BottomNav } from '../../components/tienda-v5'
import { CHECKOUT_CSS } from './checkoutV5Styles'

const money = (n) => '$' + Number(n || 0).toLocaleString('es-UY')
const bonito = (s) => (s || '').split(' ').map(w => w ? w[0] + w.slice(1).toLowerCase() : '').join(' ')
// Umbral base para el contador del resumen (espeja envio_tarifas.oneshot_umbral_gratis).
// Es un empujón: el umbral real por zona (Canelones $2.000) se aplica server-side en el paso 3.
const UMBRAL_BASE = 1000
// Centros de respaldo para abrir el mapa cuando el geocoder (OSM) no encuentra la dirección
// —típico en barrios privados de Canelones, que OSM no tiene. El cliente arrastra el pin.
const CENTRO_DEPTO = {
  montevideo: { lat: -34.9011, lng: -56.1645 },
  canelones: { lat: -34.8500, lng: -56.0000 }, // corredor Camino de los Horneros / Barra de Carrasco
  interior: { lat: -34.4000, lng: -56.2300 },
}

export default function CheckoutOneShot() {
  const navigate = useNavigate()
  const { user, profile } = useAuth()
  const { items: cartItems, clear, updateQty, removeItem } = useCart()
  const esVisitor = !user

  // Live sobre el carrito: editar cantidades en el paso 1 recalcula subtotal/envío al toque.
  const items = useMemo(() => (cartItems || []).map(it => ({
    sku: it.sku, nombre: it.nombre, presentacion: it.presentacion,
    precio_unit: Number(it.precio_unit), cantidad: Number(it.cantidad || 1), marca: it.marca,
  })), [cartItems])
  const subtotal = useMemo(() => items.reduce((s, it) => s + it.precio_unit * it.cantidad, 0), [items])

  const [paso, setPaso] = useState(1) // 1 Resumen · 2 Dirección · 3 Envío · 4 Pagar
  const [calle, setCalle] = useState('')
  const [numero, setNumero] = useState('')
  const [apto, setApto] = useState('')
  const [coords, setCoords] = useState(null)
  const [coordsExactas, setCoordsExactas] = useState(false) // pin confirmado en el mapa
  const [geocodando, setGeocodando] = useState(false)
  const [sugerencias, setSugerencias] = useState([]) // autocomplete de direcciones
  const [showSug, setShowSug] = useState(false)
  const [buscandoDir, setBuscandoDir] = useState(false)
  const [barrioGeo, setBarrioGeo] = useState(null) // barrio MVD detectado por el pin
  const [barrioManual, setBarrioManual] = useState('') // barrio MVD elegido a mano (fallback si el pin no engancha)
  const [barriosMvd, setBarriosMvd] = useState([]) // lista de barrios de Montevideo (dropdown de respaldo)
  const [departamento, setDepartamento] = useState('') // '' | 'montevideo' | 'canelones' | 'interior' (a mano)
  const [localidadesCanelones, setLocalidadesCanelones] = useState([]) // lista deliverable (Costa de Oro + privados)
  const [canelonesSel, setCanelonesSel] = useState('')
  const [localidad, setLocalidad] = useState('')
  const [ci, setCi] = useState('')
  const [envio, setEnvio] = useState(null)
  const [calcLoad, setCalcLoad] = useState(false)
  const [nombre, setNombre] = useState(profile?.nombre || '')
  const [telefono, setTelefono] = useState(profile?.phone || '')
  const [email, setEmail] = useState(profile?.email || '')
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [creando, setCreando] = useState(false)
  const [err, setErr] = useState(null)
  const [cupon, setCupon] = useState('')
  const [cuponData, setCuponData] = useState(null)   // resultado de validar_cupon (o null)
  const [cuponMsg, setCuponMsg] = useState(null)     // motivo de rechazo (no disponible, agotado, etc.)
  const [validandoCupon, setValidandoCupon] = useState(false)
  // 2026-09-11: metodo de pago + estado post-transferencia
  const [metodoPago, setMetodoPago] = useState('mercadopago') // 'mercadopago' | 'transferencia'
  const [transferData, setTransferData] = useState(null) // { pedido_id, numero_orden, monto_total, datos_transferencia, whatsapp_soporte }
  // Stock disponible por SKU (= stock_actual − stock_reservado) para topear el + de cada ítem.
  // Misma fuente que el catálogo (tienda_get_productos, SECURITY DEFINER). null = cargando.
  const [stockDisp, setStockDisp] = useState(null)
  // Umbral de envío gratis para el hint del paso 1: espeja envio_tarifas.oneshot_umbral_gratis
  // (fallback UMBRAL_BASE si la config no carga). El cobro real por zona se aplica server-side.
  const [umbralBase, setUmbralBase] = useState(UMBRAL_BASE)
  useEffect(() => {
    supabase.from('envio_tarifas').select('monto').eq('clave', 'oneshot_umbral_gratis').maybeSingle()
      .then(({ data }) => { const v = Number(data?.monto); if (v > 0) setUmbralBase(v) })
      .catch(() => {})
  }, [])
  const initedRef = useRef(false)
  const finalizandoRef = useRef(false) // true al confirmar → no rebota a /catalogo cuando clear() vacía el carrito

  // Traigo el stock disponible una vez para topear el "+" de cada ítem al máximo que hay.
  useEffect(() => {
    let cancel = false
    getProductosTienda()
      .then(rows => { if (cancel) return; const m = {}; for (const r of (rows || [])) m[r.sku] = Number(r.stock) || 0; setStockDisp(m) })
      .catch(() => { if (!cancel) setStockDisp({}) })
    return () => { cancel = true }
  }, [])

  useEffect(() => { if (!initedRef.current) { initedRef.current = true; try { initMpDeviceId() } catch {} } }, [])
  useEffect(() => { if (!items.length && !finalizandoRef.current) navigate('/catalogo', { replace: true }) }, [items.length, navigate])
  // Localidades de Canelones a las que repartimos = zona extendida (Costa de Oro + barrios privados).
  // "Si no aparece en la lista, no se envía": el desplegable es la única fuente, no hay texto libre.
  useEffect(() => {
    supabase.from('zona_barrio_geo').select('nombre').eq('zona_codigo', 'extendida').order('nombre')
      .then(({ data }) => setLocalidadesCanelones((data || []).map(r => r.nombre)))
  }, [])
  // Barrios de Montevideo (todo lo que NO es extendida) para el dropdown de respaldo: si el pin no engancha
  // el barrio (mapa caído, OSM lento, point-in-polygon falla), el cliente lo elige a mano y NO queda trancado.
  useEffect(() => {
    supabase.from('zona_barrio_geo').select('nombre').neq('zona_codigo', 'extendida').order('nombre')
      .then(({ data }) => setBarriosMvd((data || []).map(r => r.nombre)))
  }, [])

  // Telemetría del checkout (diagnóstico de abandono). Best-effort: nunca rompe el flujo. Escribe en
  // signup_errors_log (error_source='front') vía el edge público log-front-error. Antes estábamos ciegos.
  function logChk(paso, extra, nivel = 'info') {
    try {
      supabase.functions.invoke('log-front-error', {
        body: {
          nivel, paso,
          email: (email || '').trim().toLowerCase() || null,
          error_msg: extra ? JSON.stringify(extra).slice(0, 1000) : null,
          user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent : null),
        },
      }).catch(() => {})
    } catch { /* noop */ }
  }

  // Autocomplete de direcciones (Nominatim, todo Uruguay) con debounce para respetar el rate limit.
  useEffect(() => {
    // Canelones: sesgamos por "Canelones" pero NO por el barrio privado (OSM no lo tiene y ensucia).
    const ctx = departamento === 'canelones' ? 'Canelones'
      : departamento === 'montevideo' ? 'Montevideo'
      : (localidad.trim() || '')
    const q = [calle.trim(), numero.trim(), ctx].filter(Boolean).join(' ')
    if (!showSug || calle.trim().length < 4) { setSugerencias([]); setBuscandoDir(false); return }
    let cancel = false
    setBuscandoDir(true)
    const t = setTimeout(async () => {
      const res = await buscarDirecciones(q)
      if (!cancel) { setSugerencias(res); setBuscandoDir(false) }
    }, 550)
    return () => { cancel = true; clearTimeout(t) }
  }, [calle, numero, showSug, departamento, canelonesSel, localidad])

  const barrioParaEnvio = departamento === 'montevideo' ? (barrioManual || barrioGeo || null)
    : departamento === 'canelones' ? (canelonesSel || null)
    : null

  useEffect(() => {
    const listo = (departamento === 'montevideo' && (barrioManual || barrioGeo)) ||
                  (departamento === 'canelones' && canelonesSel) ||
                  (departamento === 'interior')
    if (!listo) { setEnvio(null); return }
    let cancel = false
    setCalcLoad(true)
    calcularEnvioOneshot(barrioParaEnvio, subtotal)
      .then(r => { if (!cancel) setEnvio(r) })
      .catch(() => { if (!cancel) setEnvio(null) })
      .finally(() => { if (!cancel) setCalcLoad(false) })
    return () => { cancel = true }
  }, [barrioGeo, barrioManual, departamento, canelonesSel, subtotal, barrioParaEnvio])

  // 2026-09-11: auto-aplicar el cupón del vendedor cuando el cliente entró por
  // ?ref=BIKO (o similar). Si viene con ref activo:
  //   1) Al montar: llamamos validar_codigo_vendedor — si el subtotal ya alcanza
  //      el mínimo ($1500), aplica el 10% directo. Si no, queda como "solo
  //      atribución" (muestra el badge azul en el cupón).
  //   2) Cuando el subtotal cruza el umbral (usuario suma items), reintentamos
  //      para promover de "solo atribución" a "descuento aplicado".
  // El cliente NO tiene que escribir el código a mano.
  const autoRefIntentado = useRef(false)
  useEffect(() => {
    const refCode = getRef()
    if (!refCode) return
    // Si ya hay un cupón manual del cliente (LIMPACK10 por ej), no lo pisamos.
    if (cuponData && !cuponData.vendedor) return
    // Si ya está aplicado el descuento por este mismo ref, no hacemos nada.
    if (cuponData?.vendedor && !cuponData.solo_atribucion) return
    // Al primer render intentamos siempre; después, solo si cambia el subtotal
    // (por si cruzó el mínimo).
    if (autoRefIntentado.current && subtotal < 1500) return
    autoRefIntentado.current = true
    let cancel = false
    ;(async () => {
      try {
        const { data, error } = await supabase.rpc('validar_codigo_vendedor', {
          p_codigo: refCode, p_email: email.trim().toLowerCase() || null, p_total: subtotal,
        })
        if (cancel || error) return
        if (data?.aplica_descuento) {
          setCuponData({
            ...data.cupon,
            vendedor: data.vendedor,
            beneficios: data.beneficios_extra || [],
            solo_atribucion: false,
          })
          setCupon(refCode)
        } else if (data?.es_vendedor) {
          setCuponData({
            codigo: data.codigo,
            descuento: 0,
            vendedor: data.vendedor,
            beneficios: data.beneficios_extra || [],
            motivo_no_descuento: data.cupon?.motivo,
            solo_atribucion: true,
          })
          setCupon(refCode)
        }
      } catch { /* silencio: si falla, el cliente aún puede tipearlo */ }
    })()
    return () => { cancel = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal])

  const direccionStr = [calle.trim(), numero.trim()].filter(Boolean).join(' ')
  const costo = Number(envio?.costo || 0)
  const descuentoCupon = Number(cuponData?.descuento || 0)
  const total = Math.max(0, subtotal + costo - descuentoCupon)
  const destinoLabel = departamento === 'canelones' ? `${bonito(canelonesSel)} (Canelones)`
    : departamento === 'interior' ? (localidad ? `${bonito(localidad)} (interior)` : 'Interior')
    : (barrioGeo ? `${bonito(barrioGeo)} (Montevideo)` : 'Montevideo')
  // "cuando" viene del back (hoy / mañana / el lunes — respeta el corte del sábado 10h y el salto a lunes).
  const cuando = envio?.entrega_cuando || (envio?.entrega_hoy ? 'hoy' : 'mañana')
  const entregaLabel = envio
    ? (envio.opcion === 'interior' ? `Sale ${cuando}` : `Llega ${cuando}`)
    : ''
  // Datos mínimos para poder pagar (nombre + WhatsApp + email + cédula; visitante también password + anti-bot)
  const telOk = telefono.replace(/\D/g, '').length >= 8
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())
  const ciOk = ci.replace(/\D/g, '').length >= 7
  const puedeConfirmar = !!nombre.trim() && telOk && emailOk && ciOk && (!esVisitor || !!turnstileToken)

  // Habilita "Siguiente" en el paso de dirección. En MVD el barrio sale del pin (pin obligatorio);
  // en Canelones/interior la zona sale de lo elegido a mano y el pin es opcional (no bloquea).
  // El pin es OBLIGATORIO en todos los casos: sin él la dirección de Canelones/interior es
  // imprecisa y el reparto se va a cualquier lado. En MVD además define el barrio.
  // Pin recomendado pero NO bloqueante: si el mapa no carga o el point-in-polygon no engancha, el cliente
  // igual avanza (en MVD eligiendo el barrio a mano). Antes coordsExactas era obligatorio y trancaba la venta.
  const dirBasica = !!departamento && !!calle.trim() && !!numero.trim()
  const puedeAvanzarDireccion = departamento === 'montevideo'
    ? (dirBasica && !!(barrioManual || barrioGeo))
    : departamento === 'canelones'
      ? (dirBasica && !!canelonesSel)
      : departamento === 'interior'
        ? (dirBasica && !!localidad.trim())
        : false

  // Detecta el barrio de Montevideo por el pin (point-in-polygon). Solo aplica a MVD.
  function resolverBarrio(lat, lng) {
    const b = barrioOficialDeCoords(lat, lng)
    setBarrioGeo(b)
    // Si el pin detecta el barrio, sincronizamos el dropdown (barrioManual = fuente autoritativa en MVD).
    if (b) setBarrioManual(b)
  }

  // Ciudad para sesgar el geocode según el departamento/localidad elegidos a mano (mejor precisión).
  function ciudadGeocode() {
    if (departamento === 'montevideo') return 'Montevideo, Uruguay'
    // Canelones: NO metemos el nombre del barrio (los privados no están en OSM y arruinan la
    // búsqueda de la calle). Buscamos calle + "Canelones"; la calle (ej. Camino de los Horneros) sí está.
    if (departamento === 'canelones') return 'Canelones, Uruguay'
    return `${localidad.trim() ? localidad.trim() + ', ' : ''}Uruguay`
  }

  async function geocodear() {
    setErr(null); setBarrioGeo(null); setCoordsExactas(false); setShowSug(false)
    if (!calle.trim() || !numero.trim()) { setErr('Poné calle y número.'); return }
    setGeocodando(true)
    try {
      // 1) dirección exacta → 2) localidad/departamento → 3) centro fijo de respaldo.
      // El mapa SIEMPRE abre: si el geocoder no encuentra nada (barrios privados que OSM no tiene),
      // centramos en la zona y el cliente arrastra el pin a su casa.
      let r = await geocodificar(direccionStr, ciudadGeocode())
      if (!r) {
        const [base, ciu] = departamento === 'montevideo' ? ['Montevideo', 'Uruguay']
          : departamento === 'canelones' ? [canelonesSel ? bonito(canelonesSel) : 'Ciudad de la Costa', 'Canelones, Uruguay']
          : [localidad.trim() || 'Uruguay', 'Uruguay']
        r = await geocodificar(base, ciu)
      }
      const punto = r ? { lat: r.lat, lng: r.lng } : (CENTRO_DEPTO[departamento] || CENTRO_DEPTO.montevideo)
      if (!r) { setErr('No ubicamos el número exacto. Arrastrá el pin justo sobre tu casa para confirmar.'); logChk('oneshot_geocode_fail', { direccion: direccionStr, departamento }, 'error') }
      setCoords(punto)
      resolverBarrio(punto.lat, punto.lng)
      if (departamento === 'montevideo' && !barrioOficialDeCoords(punto.lat, punto.lng)) {
        logChk('oneshot_barrio_no_detectado', { lat: punto.lat, lng: punto.lng }, 'error')
      }
    } finally { setGeocodando(false) }
  }

  // El cliente eligió una dirección del desplegable → fijamos coords y (si es MVD) el barrio.
  function elegirSugerencia(s) {
    if (s.calle) setCalle(s.calle)
    // OSM/Nominatim a veces devuelve una lista de números para un edificio
    // (ej "2975,2981" o "2975;2981"). Si el usuario ya escribió un número
    // y coincide con alguno de los de la lista, respetamos el suyo. Si no,
    // caemos al primero como fallback.
    if (s.numero) {
      const nums = String(s.numero).split(/[,;/\s-]/).map(x => x.trim()).filter(Boolean)
      const puestoPorUsuario = numero.trim()
      const elegido = puestoPorUsuario && nums.includes(puestoPorUsuario) ? puestoPorUsuario : nums[0]
      if (elegido) setNumero(elegido)
    }
    setShowSug(false); setSugerencias([]); setErr(null)
    setBarrioGeo(null)
    setCoords({ lat: s.lat, lng: s.lng }); setCoordsExactas(false)
    resolverBarrio(s.lat, s.lng)
  }

  function validarDireccion() {
    if (!departamento) return 'Elegí el departamento.'
    if (departamento === 'canelones' && !canelonesSel) return 'Elegí tu localidad de Canelones.'
    if (departamento === 'interior' && !localidad.trim()) return 'Poné la localidad de destino.'
    if (!calle.trim() || !numero.trim()) return 'Poné calle y número.'
    if (departamento === 'montevideo' && !(barrioManual || barrioGeo)) return 'Elegí tu barrio de Montevideo (o ubicá el pin en el mapa).'
    return null
  }

  async function confirmar() {
    setErr(null)
    if (!nombre.trim()) return setErr('Falta tu nombre.')
    const tel = telefono.replace(/\D/g, '')
    if (tel.length < 8) return setErr('Falta un WhatsApp válido (mínimo 8 dígitos).')
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setErr('Falta un email válido.')
    if (ci.replace(/\D/g, '').length < 7) return setErr('Falta tu cédula (la pide Mercado Pago para el pago).')

    const pedido = {
      oneshot: true, tipo: 'productos_sueltos', items,
      comprador_nombre: nombre.trim(), comprador_telefono: telefono, comprador_email: email.trim().toLowerCase(),
      entrega_barrio_geo: barrioParaEnvio || null,
      entrega_direccion: departamento === 'interior' ? `${direccionStr} — ${localidad.trim()}` : direccionStr,
      entrega_apto: apto || null, entrega_lat: coords?.lat, entrega_lng: coords?.lng,
      entrega_localidad: departamento === 'interior' ? localidad.trim() : null,
      comprador_documento: ci.replace(/\D/g, '') || null,
      costo_envio: costo, notas: null,
      cupon_codigo: (cuponData && !cuponData.solo_atribucion) ? cuponData.codigo : null,
    }

    setCreando(true)
    try {
      if (esVisitor) {
        if (!turnstileToken) throw new Error('Completá la verificación anti-bot.')
        const deviceId = await getMpDeviceId()
        logChk('oneshot_confirmar_guest', { destino: barrioParaEnvio || localidad || null, total })
        // Guest checkout: NO mandamos password → el edge crea el pedido SIN cuenta (comprador_profile_id
        // null). Los datos de contacto (nombre/WhatsApp/email) viajan en el pedido para poder contactarlo.
        const { data, error } = await supabase.functions.invoke('crear-pedido-publico', {
          body: {
            email: email.trim().toLowerCase(), nombre: nombre.trim(), telefono,
            turnstile_token: turnstileToken, device_id: deviceId || undefined,
            utm: getUTM() || undefined, ref: (cuponData?.vendedor?.slug || getRef() || undefined),
            metodo_pago: metodoPago,
            pedido,
          },
        })
        if (error) { logChk('oneshot_crear_pedido_error', { msg: String(error?.message || error) }, 'error'); throw error }
        if (data?.error) { logChk('oneshot_crear_pedido_error', { msg: data.error, detalle: data.detalle }, 'error'); throw new Error(data.error + (data.detalle ? ': ' + JSON.stringify(data.detalle) : '')) }

        try { localStorage.setItem('limpack_visitor_email', email.trim().toLowerCase()) } catch {}
        finalizandoRef.current = true

        // Transferencia: mostrar pantalla con datos MP (paso 5)
        if (data?.metodo_pago === 'transferencia') {
          setTransferData({
            pedido_id: data.pedido_id,
            numero_orden: data.numero_orden,
            monto_total: data.monto_total,
            datos_transferencia: data.datos_transferencia,
            whatsapp_soporte: data.whatsapp_soporte,
          })
          try { clear() } catch {}
          setPaso(5)
          setCreando(false)
          return
        }

        // MP: redirect a la pasarela
        if (!data.init_point) { logChk('oneshot_sin_init_point', {}, 'error'); throw new Error('MercadoPago no devolvió URL de pago.') }
        try { clear() } catch {}
        window.location.href = data.init_point
        return
      }
      const pedidoRes = await crearPedidoOneshot(user.id, pedido)
      const pid = Array.isArray(pedidoRes) ? pedidoRes[0]?.id : pedidoRes?.id
      finalizandoRef.current = true
      try { clear() } catch {}
      if (pid) navigate(`/cliente/pagar-pedido/${pid}`, { replace: true })
      else navigate('/gracias', { replace: true })
    } catch (e) {
      setErr(e.message || String(e)); setCreando(false)
    }
  }

  async function aplicarCupon() {
    const code = cupon.trim()
    if (!code) return
    setValidandoCupon(true); setCuponMsg(null)
    try {
      // 2026-09-11: RPC unificada — cupón + vendedor. Si es código de vendedor sin
      // llegar al mínimo, igual se atribuye la venta y se habilita transferencia.
      const { data, error } = await supabase.rpc('validar_codigo_vendedor', {
        p_codigo: code, p_email: email.trim().toLowerCase() || null, p_total: subtotal,
      })
      if (error) throw error

      if (data?.aplica_descuento) {
        // Cupón válido + (posiblemente) vendedor
        setCuponData({
          ...data.cupon,
          vendedor: data.vendedor,
          beneficios: data.beneficios_extra || [],
          solo_atribucion: false,
        })
        setCuponMsg(null)
      } else if (data?.es_vendedor) {
        // Vendedor OK pero cupón NO aplica (min compra u otro). Igual atribuir.
        setCuponData({
          codigo: data.codigo,
          descuento: 0,
          vendedor: data.vendedor,
          beneficios: data.beneficios_extra || [],
          motivo_no_descuento: data.cupon?.motivo,
          solo_atribucion: true,
        })
        setCuponMsg(null)
      } else {
        setCuponData(null)
        setCuponMsg(data?.cupon?.motivo || 'Este código no está disponible.')
      }
    } catch (e) {
      setCuponData(null); setCuponMsg('No pudimos validar el código. Probá de nuevo.')
    } finally { setValidandoCupon(false) }
  }
  function quitarCupon() { setCuponData(null); setCuponMsg(null); setCupon('') }

  const STEPS = ['Resumen', 'Dirección', 'Envío', 'Pagar']

  return (
    <div className="tv5-page-checkout">
      <style>{CHECKOUT_CSS}</style>
      <style>{CF_STYLES}</style>
      <style>{`@media(max-width:899px){.tv5-page-checkout{padding-bottom:calc(76px + env(safe-area-inset-bottom,0px))}}`}</style>
      <TopNav accent="teal" />

      <div className="cf-shell">
        <div className="cf-hero">
          <img src="/limpackflex-logo-white.svg" alt="LimpackFlex" className="cf-hero-logo" />
          <div className="cf-hero-pill"><span className="cf-hero-dot"></span>Paso {paso} de 4 · Tu pedido</div>
          <h1>Tu pedido</h1>
        </div>

        <div className="cf-stepper">
          {STEPS.map((label, i) => {
            const idx = i + 1, isDone = paso > idx, isActive = paso === idx
            return (
              <button type="button" key={i} disabled={!isDone} onClick={() => isDone && setPaso(idx)}
                className={`cf-step ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}
                style={{ background: 'transparent', border: 0, font: 'inherit', cursor: isDone ? 'pointer' : 'default', padding: 0 }}>
                <div className="cf-step-num">{isDone ? '✓' : idx}</div>
                <div className="cf-step-label">{label}</div>
              </button>
            )
          })}
        </div>

        {/* PASO 1 · Resumen */}
        {paso === 1 && (
          <div className="cf-card">
            <h2 className="cf-h2">Lo que llevás</h2>
            <ul className="cf-items">
              {items.map(it => (
                <li key={it.sku}>
                  <div className="cf-item-info">
                    <strong>{it.nombre}</strong>
                    {it.presentacion && <span> · {it.presentacion}</span>}
                    {it.marca && <span className="cf-item-marca">{it.marca}</span>}
                  </div>
                  {(() => {
                    const max = stockDisp ? (stockDisp[it.sku] ?? it.cantidad) : it.cantidad
                    const topeado = it.cantidad >= max
                    return (
                      <div className="cf-item-cant-wrap">
                        <div className="cf-item-cant">
                          <button type="button"
                            aria-label={it.cantidad <= 1 ? 'Eliminar del carrito' : 'Restar uno'}
                            className={it.cantidad <= 1 ? 'cf-cant-trash' : undefined}
                            title={it.cantidad <= 1 ? 'Eliminar del carrito' : undefined}
                            onClick={() => (it.cantidad <= 1 ? removeItem(it.sku) : updateQty(it.sku, it.cantidad - 1))}>{it.cantidad <= 1 ? '🗑' : '−'}</button>
                          <span>{it.cantidad}</span>
                          <button type="button" aria-label="Sumar uno" onClick={() => updateQty(it.sku, it.cantidad + 1)} disabled={topeado} title={topeado ? 'Es todo el stock que hay' : undefined}>+</button>
                        </div>
                        {stockDisp && topeado && max > 0 && <div className="cf-item-cant-max">máx {max}</div>}
                      </div>
                    )
                  })()}
                  <div className="cf-item-precio">{money(it.precio_unit * it.cantidad)}</div>
                </li>
              ))}
            </ul>
            <div className="cf-totales">
              <div><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
              {descuentoCupon > 0 && (
                <div className="cf-totales-desc"><span>Descuento ({cuponData.codigo})</span><span>-{money(descuentoCupon)}</span></div>
              )}
              <div className="cf-totales-info">Precios con IVA incluido. El envío se calcula por tu dirección en el paso siguiente.</div>
            </div>

            {/* Contador de envío gratis — empuja a subir el carrito ya desde el resumen */}
            {subtotal >= umbralBase ? (
              <div className="cf-envgratis ok">🎉 Vas por envío gratis — se confirma con tu dirección</div>
            ) : (
              <div className="cf-envgratis">
                <div className="cf-envgratis-txt">🚚 Te falta <strong>{money(umbralBase - subtotal)}</strong> para <strong>envío gratis</strong> según tu zona</div>
                <div className="cf-envgratis-bar"><span style={{ width: Math.min(100, Math.round(subtotal / umbralBase * 100)) + '%' }} /></div>
              </div>
            )}

            {/* Cupón de descuento — lo ingresa el cliente */}
            <div className="cf-cupon">
              {cuponData ? (
                <div className="cf-cupon-ok" style={cuponData.solo_atribucion ? { background: '#eff6ff', borderColor: '#bfdbfe' } : undefined}>
                  <span className="cf-cupon-check" style={cuponData.solo_atribucion ? { background: '#3b82f6' } : undefined}>
                    {cuponData.solo_atribucion ? 'i' : '✓'}
                  </span>
                  <div className="cf-cupon-ok-txt">
                    {cuponData.solo_atribucion ? (
                      <>
                        <strong>{cuponData.codigo} reconocido{cuponData.vendedor ? ` — ${cuponData.vendedor.nombre}` : ''}</strong>
                        <span>
                          {cuponData.motivo_no_descuento || 'Aún no aplica el descuento'}
                          {(cuponData.beneficios || []).includes('transferencia_bancaria') && ' · Tendrás transferencia bancaria habilitada al pagar.'}
                        </span>
                      </>
                    ) : (
                      <>
                        <strong>{cuponData.codigo} aplicado</strong>
                        <span>
                          Ahorrás {money(descuentoCupon)}
                          {cuponData.vendedor ? ` · Traído por ${cuponData.vendedor.nombre}` : ''}
                          {(cuponData.beneficios || []).includes('transferencia_bancaria') && ' · Transferencia bancaria habilitada'}
                        </span>
                      </>
                    )}
                  </div>
                  <button type="button" className="cf-cupon-quit" onClick={quitarCupon}>Quitar</button>
                </div>
              ) : (
                <>
                  <div className="cf-cupon-lbl">🏷️ ¿Tenés un código de descuento o de referido?</div>
                  <div className="cf-cupon-row">
                    <input value={cupon} onChange={e => { setCupon(e.target.value); setCuponMsg(null) }}
                      placeholder="Ingresá tu código"
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); aplicarCupon() } }} />
                    <button type="button" className="cf-cupon-ap" onClick={aplicarCupon} disabled={validandoCupon || !cupon.trim()}>
                      {validandoCupon ? '...' : 'Aplicar'}
                    </button>
                  </div>
                  {cuponMsg && <div className="cf-cupon-msg">{cuponMsg}</div>}
                </>
              )}
            </div>

            <button type="button" className="cf-btn-seguir" onClick={() => navigate('/catalogo')}>
              + Seguir sumando productos
            </button>

            {items.length > 0 && (
              <button type="button" className="cf-btn-vaciar" onClick={() => { if (window.confirm('¿Vaciar el carrito? Se quitan todos los productos.')) { try { clear() } catch {} logChk('oneshot_vaciar_carrito', { n_items: items.length }) } }}>
                Vaciar carrito
              </button>
            )}

            <div className="cf-nav">
              <button className="cf-btn-back" onClick={() => navigate('/catalogo')}>← Cancelar</button>
              <button className="cf-btn-next" onClick={() => { logChk('oneshot_paso2_direccion', { subtotal, n_items: items.length }); setPaso(2) }} disabled={subtotal <= 0}>Siguiente →</button>
            </div>
          </div>
        )}

        {/* PASO 2 · Dirección */}
        {paso === 2 && (
          <div className="cf-card">
            <h2 className="cf-h2">¿A dónde te lo enviamos?</h2>

            {/* Departamento — a mano, primero */}
            <label className="cf-field"><b>Departamento</b>
              <select value={departamento} onChange={e => {
                setDepartamento(e.target.value)
                setBarrioGeo(null); setCanelonesSel(''); setLocalidad('')
                setCoords(null); setCoordsExactas(false); setShowSug(false); setErr(null)
              }}>
                <option value="">— Elegí tu departamento —</option>
                <option value="montevideo">Montevideo</option>
                <option value="canelones">Canelones (costa y barrios privados)</option>
                <option value="interior">Otra zona / interior del país</option>
              </select>
            </label>

            {/* Localidad — a mano, según el departamento */}
            {departamento === 'canelones' && (
              <>
                <label className="cf-field"><b>Localidad</b>
                  <select value={canelonesSel} onChange={e => setCanelonesSel(e.target.value)}>
                    <option value="">— Seleccioná tu localidad —</option>
                    {localidadesCanelones.map(p => <option key={p} value={p}>{bonito(p)}</option>)}
                  </select>
                </label>
                <div className="cf-hint">Repartimos en estas localidades y barrios privados de Canelones. Si la tuya no está, elegí «Otra zona / interior del país».</div>
              </>
            )}
            {departamento === 'interior' && (
              <>
                <label className="cf-field"><b>Localidad de destino <span className="cf-req">*</span></b><input value={localidad} onChange={e => setLocalidad(e.target.value)} placeholder="Ej: Maldonado, Colonia, Salto…" /></label>
                <div className="cf-hint">Despacho a la Terminal Tres Cruces. La encomienda se paga al retirar en destino. Te pedimos la cédula en el paso de pago.</div>
              </>
            )}

            {/* Calle / Número / Apto — aparecen al elegir el departamento */}
            {departamento && (
              <>
                <div className="cf-row-calle-nro">
                  <label className="cf-field"><b>Calle</b><input value={calle} autoComplete="off" onChange={e => { setCalle(e.target.value); setShowSug(true) }} placeholder="Empezá a escribir tu calle…" /></label>
                  <label className="cf-field"><b>Número</b><input value={numero} autoComplete="off" onChange={e => { setNumero(e.target.value); setShowSug(true) }} placeholder="2500" /></label>
                  <label className="cf-field"><b>Apto</b><input value={apto} onChange={e => setApto(e.target.value)} placeholder="401" /></label>
                </div>

                {/* Desplegable de direcciones mientras escribe */}
                {showSug && (buscandoDir || sugerencias.length > 0) && (
                  <div className="cf-autocomplete">
                    {buscandoDir && <div className="cf-ac-load">Buscando direcciones…</div>}
                    {sugerencias.map((s, i) => {
                      // OSM devuelve "2975,2981" cuando un edificio tiene varios números registrados:
                      // si el usuario ya escribió un número que está en la lista, mostramos el suyo;
                      // si no, mostramos el primero como fallback.
                      const numsOSM = s.numero ? String(s.numero).split(/[,;/\s-]/).map(x => x.trim()).filter(Boolean) : []
                      const puestoPorUsuario = numero.trim()
                      const nroLimpio = puestoPorUsuario && numsOSM.includes(puestoPorUsuario) ? puestoPorUsuario : (numsOSM[0] || '')
                      const titulo = s.calle ? `${s.calle}${nroLimpio ? ' ' + nroLimpio : ''}` : (s.display || '').split(',')[0]
                      return (
                        <button type="button" key={i} className="cf-ac-item" onClick={() => elegirSugerencia(s)}>
                          <span className="cf-ac-main">{titulo}</span>
                          <span className="cf-ac-sub">{s.localidad || s.display}</span>
                        </button>
                      )
                    })}
                  </div>
                )}

                <button type="button" className="cf-btn-secondary" onClick={geocodear} disabled={geocodando}>
                  {geocodando ? 'Buscando…' : coords ? '🔄 Recargar mapa' : '📍 Ubicar en el mapa'}
                </button>
                {!coords && <div className="cf-hint">Confirmar la ubicación en el mapa es obligatorio: así el reparto llega exacto, sobre todo en Canelones.</div>}

                {coords && (
                  <>
                    <div style={{ marginTop: 12 }}>
                      <MapPin lat={coords.lat} lng={coords.lng} height={220}
                        onChange={(lat, lng) => { setCoords({ lat, lng }); setCoordsExactas(false); resolverBarrio(lat, lng) }} />
                    </div>
                    <label className={`cf-confirm-pin ${coordsExactas ? 'ok' : 'pend'}`}>
                      <input type="checkbox" checked={coordsExactas} onChange={e => setCoordsExactas(e.target.checked)} />
                      <span>{coordsExactas ? 'Pin en la ubicación correcta' : 'Mové el pin si hace falta y confirmá tu ubicación'}</span>
                    </label>
                  </>
                )}

                {departamento === 'montevideo' && (
                  <>
                    <label className="cf-field" style={{ marginTop: 14 }}><b>Barrio</b>
                      <select value={barrioManual} onChange={e => { setBarrioManual(e.target.value); setErr(null) }}>
                        <option value="">— Elegí tu barrio —</option>
                        {barriosMvd.map(b => <option key={b} value={b}>{bonito(b)}</option>)}
                      </select>
                    </label>
                    {barrioGeo && barrioManual === barrioGeo
                      ? <div className="cf-info">Detectamos tu barrio por el mapa: <strong>{bonito(barrioGeo)}</strong>. Si no es correcto, cambialo arriba.</div>
                      : <div className="cf-hint">Ubicá el pin en el mapa y lo detectamos solo, o elegí tu barrio de la lista.</div>}
                  </>
                )}
              </>
            )}

            {err && <div className="cf-err">{err}</div>}
            <div className="cf-nav">
              <button className="cf-btn-back" onClick={() => setPaso(1)}>← Atrás</button>
              <button className="cf-btn-next" disabled={!puedeAvanzarDireccion} onClick={() => { const e = validarDireccion(); if (e) { setErr(e); logChk('oneshot_bloqueo_direccion', { motivo: e, departamento }, 'error'); return } setErr(null); logChk('oneshot_paso3_envio', { destino: barrioParaEnvio || localidad || null }); setPaso(3) }}>Siguiente →</button>
            </div>
          </div>
        )}

        {/* PASO 3 · Envío */}
        {paso === 3 && (
          <div className="cf-card">
            <h2 className="cf-h2">Tu envío</h2>
            {calcLoad && <div className="cf-loading">Calculando envío…</div>}
            {envio && !calcLoad && (() => {
              const cuandoTxt = envio.entrega_cuando || (envio.entrega_hoy ? 'hoy' : 'mañana')
              const entregaTxt = (envio.opcion === 'interior' ? `Sale ${cuandoTxt}` : `Llega ${cuandoTxt}`) + ' 🚚'
              const falta = Math.max(0, Number(envio.umbral || 0) - subtotal)
              const pct = envio.umbral ? Math.min(100, Math.round(subtotal / Number(envio.umbral) * 100)) : 0
              return (
                <>
                  <div className={`cf-fecha-resumen ${envio.entrega_hoy ? 'is-en-ruta' : 'is-fuera'}`}>
                    <div>
                      <div className="cf-fecha-resumen-dia" style={{ fontSize: 18 }}>{entregaTxt}</div>
                      <div className="cf-fecha-resumen-tag" style={{ marginTop: 4 }}>{envio.etiqueta}</div>
                    </div>
                    <div className="cf-fecha-resumen-precio">
                      {envio.gratis ? <span className="cf-monto-ok">GRATIS</span> : money(envio.costo)}
                    </div>
                  </div>
                  <div className="cf-banner-corte">{envio.detalle}</div>

                  {/* Contador de envío gratis según la zona */}
                  {envio.gratis ? (
                    <div className="cf-envgratis ok">🎉 ¡Tenés envío gratis!</div>
                  ) : envio.umbral ? (
                    <div className="cf-envgratis">
                      <div className="cf-envgratis-txt">🚚 Agregá <strong>{money(falta)}</strong> y tu envío es <strong>gratis</strong></div>
                      <div className="cf-envgratis-bar"><span style={{ width: pct + '%' }} /></div>
                    </div>
                  ) : null}

                  <div className="cf-totales" style={{ marginTop: 16 }}>
                    <div><span>Productos</span><span>{money(subtotal)}</span></div>
                    {descuentoCupon > 0 && (
                      <div className="cf-totales-desc"><span>Descuento ({cuponData.codigo})</span><span>-{money(descuentoCupon)}</span></div>
                    )}
                    <div><span>Envío</span><span>{envio.gratis ? 'Gratis' : money(costo)}</span></div>
                    <div style={{ marginTop: 8 }}><span><strong>Total</strong></span><strong>{money(total)}</strong></div>
                    <div className="cf-totales-info">Precios con IVA incluido.</div>
                  </div>
                </>
              )
            })()}
            {!envio && !calcLoad && <div className="cf-err">No pudimos calcular el envío. Volvé al paso anterior y revisá la dirección.</div>}
            <div className="cf-nav">
              <button className="cf-btn-back" onClick={() => setPaso(2)}>← Atrás</button>
              <button className="cf-btn-next" onClick={() => { logChk('oneshot_paso4_pagar', { destino: barrioParaEnvio || localidad || null, gratis: !!envio?.gratis }); setPaso(4) }} disabled={!envio}>Siguiente →</button>
            </div>
          </div>
        )}

        {/* PASO 4 · Pagar */}
        {paso === 4 && (
          <div className="cf-card">
            <h2 className="cf-h2">Tus datos y pago</h2>

            {/* Recap del envío — para que el cliente confirme a dónde y cuándo antes de pagar */}
            <div className={`cf-fecha-resumen ${envio?.entrega_hoy ? 'is-en-ruta' : 'is-fuera'}`} style={{ marginBottom: 16 }}>
              <div>
                <div className="cf-fecha-resumen-tag">Envío a</div>
                <div className="cf-fecha-resumen-dia" style={{ fontSize: 15 }}>{direccionStr}{apto ? `, ${apto}` : ''}</div>
                <div className="cf-banner-corte" style={{ marginTop: 2 }}>{destinoLabel} · {entregaLabel} · {envio?.etiqueta}</div>
              </div>
              <div className="cf-fecha-resumen-precio">{envio?.gratis ? <span className="cf-monto-ok">Gratis</span> : money(costo)}</div>
            </div>

            <label className="cf-field"><b>Nombre y apellido <span className="cf-req">*</span></b><input value={nombre} onChange={e => setNombre(e.target.value)} /></label>
            <div className="cf-row-2">
              <label className="cf-field"><b>WhatsApp <span className="cf-req">*</span></b><input value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="099 123 456" /></label>
              <label className="cf-field"><b>Email <span className="cf-req">*</span></b><input value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" /></label>
            </div>
            <label className="cf-field"><b>Cédula de identidad <span className="cf-req">*</span></b><input value={ci} onChange={e => setCi(e.target.value)} placeholder="1.234.567-8" inputMode="numeric" /></label>
            <div className="cf-hint">La pide Mercado Pago para procesar el pago{departamento === 'interior' ? ' · también la necesitás para retirar la encomienda en la terminal' : ''}.</div>
            {esVisitor && (
              <>
                <div className="cf-hint" style={{ marginTop: -4 }}>No necesitás crear cuenta: con tu nombre, WhatsApp y email alcanza para tu pedido.</div>
                <div style={{ marginBottom: 12 }}><TurnstileWidget action="checkout-oneshot" onToken={setTurnstileToken} /></div>
              </>
            )}

            <h3 className="cf-h3">Forma de pago</h3>
            {(() => {
              const refDelLink = getRef()
              const puedeTransferir = !!cuponData?.vendedor || !!refDelLink
              const nombreVendedor = cuponData?.vendedor?.nombre || (refDelLink ? refDelLink.toUpperCase() : '')
              const styBase = { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', border: '1.5px solid #e2e8f0', borderRadius: 12, cursor: 'pointer', marginBottom: 8, background: '#fff', transition: 'border-color .15s, background .15s' }
              const styElegido = { borderColor: '#00A8B5', background: 'rgba(0,168,181,0.06)', boxShadow: '0 0 0 3px rgba(0,168,181,0.08)' }
              return (
                <>
                  <label style={{ ...styBase, ...(metodoPago === 'mercadopago' ? styElegido : {}) }}>
                    <input type="radio" name="metodoPago" value="mercadopago" checked={metodoPago === 'mercadopago'} onChange={() => setMetodoPago('mercadopago')} style={{ width: 18, height: 18, accentColor: '#00A8B5', flexShrink: 0, margin: 0 }} />
                    <img src="/mp-logo.svg" alt="Mercado Pago" style={{ height: 22, width: 'auto', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a3461', lineHeight: 1.2 }}>Mercado Pago</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Tarjeta, débito, Abitab, Redpagos y transferencia</div>
                    </div>
                  </label>
                  {puedeTransferir && (
                    <label style={{ ...styBase, ...(metodoPago === 'transferencia' ? styElegido : {}) }}>
                      <input type="radio" name="metodoPago" value="transferencia" checked={metodoPago === 'transferencia'} onChange={() => setMetodoPago('transferencia')} style={{ width: 18, height: 18, accentColor: '#00A8B5', flexShrink: 0, margin: 0 }} />
                      <div style={{ fontSize: 22, flexShrink: 0, width: 26, textAlign: 'center' }}>🏦</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1a3461', lineHeight: 1.2 }}>Transferencia bancaria</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Habilitada porque venís con <strong style={{ color: '#00A8B5' }}>{nombreVendedor}</strong>. Cuenta MP: LIMPACK SAS.</div>
                      </div>
                    </label>
                  )}
                  {metodoPago === 'mercadopago' ? (
                    <div className="cf-info" style={{ marginTop: 10 }}>Al confirmar te llevamos a <strong>Mercado Pago</strong> para completar el pago de forma segura.</div>
                  ) : (
                    <div style={{ marginTop: 10, padding: '12px 14px', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', borderLeft: '4px solid #f59e0b', borderRadius: 8, fontSize: 13, color: '#fde68a', lineHeight: 1.45 }}>
                      Al confirmar te mostramos los <strong style={{ color: '#fef3c7' }}>datos para transferir</strong>. <strong style={{ color: '#fef3c7' }}>El envío no se procesa hasta que confirmemos tu pago.</strong>
                    </div>
                  )}
                </>
              )
            })()}

            <div className="cf-resumen">
              <div className="cf-row"><span>Productos</span><span className="cf-row-val">{money(subtotal)}</span></div>
              {descuentoCupon > 0 && (
                <div className="cf-row"><span>Descuento ({cuponData.codigo})</span><span className="cf-row-val" style={{ color: '#15803d' }}>-{money(descuentoCupon)}</span></div>
              )}
              <div className="cf-row"><span>Envío ({envio?.etiqueta})</span><span className="cf-row-val">{envio?.gratis ? 'Gratis' : money(costo)}</span></div>
              <div className="cf-row is-big"><span>Total</span><span className="cf-row-val">{money(total)}</span></div>
              <div className="cf-totales-info" style={{ paddingTop: 8 }}>Precios con IVA incluido.</div>
            </div>

            {err && <div className="cf-err">{err}</div>}
            <div className="cf-nav">
              <button className="cf-btn-back" onClick={() => setPaso(3)}>← Atrás</button>
              <button className="cf-btn-next" onClick={confirmar} disabled={creando || !puedeConfirmar}>{creando ? 'Procesando…' : (metodoPago === 'transferencia' ? 'Continuar →' : 'Ir a pagar →')}</button>
            </div>
          </div>
        )}

        {/* PASO 5 · Datos para transferir (solo cuando el pedido se creó como transferencia) */}
        {paso === 5 && transferData && (
          <div className="cf-card">
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 44, marginBottom: 8 }}>🏦</div>
              <h2 className="cf-h2" style={{ margin: '0 0 6px' }}>Datos para transferir</h2>
              <div style={{ fontSize: 14, color: '#64748b' }}>Pedido <strong style={{ color: '#1a3461' }}>{transferData.numero_orden}</strong> · Total: <strong style={{ color: '#1a3461' }}>{money(transferData.monto_total)}</strong></div>
            </div>

            <div style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', borderLeft: '4px solid #f59e0b', padding: '14px 16px', borderRadius: 10, marginBottom: 18, fontSize: 14, color: '#fde68a', lineHeight: 1.45 }}>
              <strong style={{ color: '#fef3c7' }}>⚠ Importante:</strong> el envío no se procesa hasta que confirmemos tu pago. Transferí y avisanos por WhatsApp o subí el comprobante para agilizar.
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>💳 Cuenta Mercado Pago</div>
              {[
                ['Titular', transferData.datos_transferencia.titular],
                ['N° cuenta', transferData.datos_transferencia.cuenta],
                ['Alias', transferData.datos_transferencia.alias],
                ['Banco', transferData.datos_transferencia.banco],
                ['Monto', money(transferData.monto_total)],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>{k}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong style={{ fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 14, color: '#1a3461' }}>{v}</strong>
                    <button type="button" onClick={() => { navigator.clipboard.writeText(String(v)); }} style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, padding: '3px 8px', fontSize: 11, color: '#1a3461', cursor: 'pointer', fontFamily: 'inherit' }}>Copiar</button>
                  </span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a3461', marginBottom: 10 }}>¿Ya transferiste?</div>
              <a
                href={`https://wa.me/${transferData.whatsapp_soporte}?text=${encodeURIComponent(`Hola! Ya hice la transferencia por el pedido ${transferData.numero_orden} (${money(transferData.monto_total)}). Te paso el comprobante.`)}`}
                target="_blank" rel="noreferrer"
                style={{ display: 'block', background: '#25D366', color: '#fff', padding: '14px 16px', borderRadius: 10, textAlign: 'center', textDecoration: 'none', fontWeight: 700, marginBottom: 10 }}
              >
                📱 Enviarme el comprobante por WhatsApp
              </a>
              <ComprobanteUploader pedidoId={transferData.pedido_id} numeroOrden={transferData.numero_orden} />
            </div>

            <div style={{ marginTop: 24, padding: 14, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: 10, fontSize: 13, color: '#bfdbfe', textAlign: 'center', lineHeight: 1.5 }}>
              <strong style={{ color: '#dbeafe' }}>Guardá estos datos.</strong> Te enviamos también por email a {email}. Cualquier consulta: hola@limpack.uy 💚
            </div>
          </div>
        )}

        <div className="cf-privacy">🔒 Tus datos están protegidos. Solo los usamos para tu pedido.</div>
      </div>

      <BottomNav active="carrito" />
    </div>
  )
}

// 2026-09-11: Subir comprobante de transferencia al bucket comprobantes-transferencia.
// Al cliente le llega ya autenticado como el user reciente creado en el checkout.
function ComprobanteUploader({ pedidoId, numeroOrden }) {
  const [uploading, setUploading] = useState(false)
  const [ok, setOk] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { setError('El archivo no puede superar 5 MB'); return }
    setUploading(true); setError(null)
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const path = `${pedidoId}/${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage.from('comprobantes-transferencia').upload(path, file, {
        contentType: file.type, upsert: true,
      })
      if (upErr) throw upErr
      const { data: pub } = supabase.storage.from('comprobantes-transferencia').getPublicUrl(path)
      // Aunque el bucket sea privado, guardamos el path como URL: el admin lo firma cuando accede.
      const publicUrl = pub?.publicUrl || path
      const { error: updErr } = await supabase.from('pedidos').update({
        comprobante_transferencia_url: publicUrl,
        comprobante_transferencia_uploaded_at: new Date().toISOString(),
      }).eq('id', pedidoId)
      if (updErr) throw updErr
      setOk(true)
    } catch (e) {
      setError(e.message || 'No pudimos subir el archivo. Probá enviarlo por WhatsApp.')
    } finally {
      setUploading(false)
    }
  }

  if (ok) return (
    <div style={{ background: '#d1fae5', border: '1px solid #a7f3d0', color: '#065f46', padding: '14px 16px', borderRadius: 10, textAlign: 'center', fontWeight: 600 }}>
      ✓ Comprobante recibido. Te avisamos por WhatsApp/email cuando confirmemos el pago.
    </div>
  )

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handleFile} style={{ display: 'none' }} />
      <button
        type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
        style={{ display: 'block', width: '100%', background: '#fff', color: '#1a3461', border: '2px dashed #cbd5e1', padding: '14px 16px', borderRadius: 10, textAlign: 'center', fontWeight: 600, cursor: uploading ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 14 }}
      >
        {uploading ? 'Subiendo…' : '📎 O subí el comprobante acá (foto o PDF)'}
      </button>
      {error && <div style={{ marginTop: 8, fontSize: 13, color: '#991b1b' }}>{error}</div>}
    </div>
  )
}

// ── Estilos cf-* copiados de CheckoutFlow (para que el UX sea idéntico). El wrapper
// .tv5-page-checkout + CHECKOUT_CSS los repinta en tema oscuro v5. ──────────────────────
const CF_STYLES = `
:root { --cf-navy:#1a3461; --cf-blue:#1a5cb8; --cf-teal:#00A8B5; --cf-cream:#F8FAFC; --cf-line:#e2e8f0; --cf-muted:#64748b; }
.cf-shell { max-width: 760px; margin: 0 auto; padding: 24px 16px 60px; font-family: 'Inter', system-ui, sans-serif; color: var(--cf-navy) }
.cf-hero { text-align: center; margin-bottom: 22px; padding: 28px 16px 32px; background: linear-gradient(135deg, #1a3461 0%, #1a5cb8 60%, #00A8B5 100%); color: #fff; border-radius: 18px }
.cf-hero-logo { display: block; height: 30px; width: auto; margin: 0 auto 14px }
.cf-hero-pill { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.18); padding: 6px 14px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 12px }
.cf-hero-dot { width: 7px; height: 7px; background: var(--cf-teal); border-radius: 50%; animation: pulse 1.6s infinite }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
.cf-hero h1 { font-size: clamp(24px, 5vw, 36px); font-weight: 900; letter-spacing: -.02em; margin: 0 }
.cf-stepper { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; margin-bottom: 18px }
.cf-step { text-align: center; opacity: .45 }
.cf-step.active, .cf-step.done { opacity: 1 }
.cf-step-num { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: #e2e8f0; color: #64748b; font-weight: 800; font-size: 13px; margin-bottom: 4px }
.cf-step.active .cf-step-num { background: var(--cf-teal); color: var(--cf-navy) }
.cf-step.done .cf-step-num { background: #22c55e; color: #fff }
.cf-step-label { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .05em }
.cf-step.active .cf-step-label { color: var(--cf-navy) }
.cf-card { background: #fff; border-radius: 18px; padding: 24px; box-shadow: 0 8px 40px rgba(26,52,97,.08); border: 1px solid rgba(26,52,97,.05) }
.cf-h2 { font-size: 22px; font-weight: 900; color: var(--cf-navy); letter-spacing: -.01em; margin: 0 0 18px }
.cf-hint { font-size: 12px; color: var(--cf-muted); margin: 6px 0 14px; line-height: 1.5; font-style: italic }
.cf-loading { color: var(--cf-muted); font-size: 14px; padding: 16px 0 }
.cf-items { list-style: none; padding: 0; margin: 12px 0 16px; display: flex; flex-direction: column; gap: 10px }
.cf-items li { display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 12px; padding: 12px; background: #f8fafc; border: 1px solid var(--cf-line); border-radius: 12px; font-size: 14px }
.cf-item-info strong { color: var(--cf-navy) }
.cf-item-info span { color: var(--cf-muted) }
.cf-item-marca { display: inline-block; font-size: 11px; padding: 2px 8px; background: #fff; border: 1px solid var(--cf-line); border-radius: 999px; margin-left: 6px }
.cf-item-cant { display: flex; align-items: center; gap: 2px; background: #fff; border: 1px solid var(--cf-line); border-radius: 999px; padding: 2px 4px }
.cf-item-cant span { font-weight: 800; min-width: 22px; text-align: center }
.cf-item-cant button { background: transparent; border: 0; color: var(--cf-navy); font-weight: 800; font-size: 18px; line-height: 1; cursor: pointer; padding: 4px 9px; border-radius: 999px; font-family: inherit }
.cf-item-cant button:hover:not(:disabled) { background: rgba(0,168,181,.12) }
.cf-item-cant button:disabled { opacity: .3; cursor: not-allowed }
.cf-item-cant-wrap { display: flex; flex-direction: column; align-items: center; gap: 3px }
.cf-item-cant-max { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .04em; white-space: nowrap }
.cf-item-precio { font-weight: 900; color: var(--cf-navy); white-space: nowrap }
.cf-totales { margin-top: 18px; padding: 14px 16px; background: #f8fafc; border-radius: 12px }
.cf-totales > div:not(.cf-totales-info) { display: flex; justify-content: space-between; align-items: baseline; font-size: 15px }
.cf-totales > div strong { font-size: 20px; font-weight: 900; color: var(--cf-navy) }
.cf-totales-info { font-size: 12px; color: var(--cf-muted); margin-top: 8px }
.cf-field { display: block; margin-bottom: 14px }
.cf-field b { display: block; font-size: 11px; font-weight: 800; color: var(--cf-navy); letter-spacing: .08em; text-transform: uppercase; margin-bottom: 6px }
.cf-req { color: #dc2626 }
.cf-card input, .cf-card select, .cf-card textarea { width: 100%; box-sizing: border-box; padding: 11px 13px; font-size: 14px; border: 1.5px solid var(--cf-line); border-radius: 10px; background: #fff; color: var(--cf-navy); font-family: inherit; transition: border-color .15s, box-shadow .15s }
.cf-card input:focus, .cf-card select:focus, .cf-card textarea:focus { outline: none; border-color: var(--cf-teal); box-shadow: 0 0 0 3px rgba(0,196,212,.15) }
.cf-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
.cf-row-calle-nro { display: grid; grid-template-columns: 3fr 1fr 1fr; gap: 12px }
@media (max-width: 520px) { .cf-row-2 { grid-template-columns: 1fr } .cf-row-calle-nro { grid-template-columns: 1fr 1fr; } .cf-row-calle-nro > *:first-child { grid-column: 1 / -1 } }
.cf-info { background: #f0fcfd; border: 1px solid #b3eef3; border-radius: 10px; padding: 12px 14px; font-size: 13px; color: var(--cf-navy); margin-bottom: 12px }
.cf-err { background: #fef2f2; border: 1px solid #fca5a5; border-radius: 10px; padding: 12px 14px; font-size: 13px; color: #991b1b; margin-top: 12px }
.cf-btn-secondary { padding: 10px 18px; font-size: 13px; font-weight: 700; background: #fff; color: var(--cf-navy); border: 1.5px solid var(--cf-navy); border-radius: 999px; cursor: pointer; font-family: inherit; transition: background .15s, color .15s }
.cf-btn-secondary:hover:not(:disabled) { background: var(--cf-navy); color: #fff }
.cf-btn-secondary:disabled { opacity: .5; cursor: not-allowed }
.cf-fecha-resumen { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 12px; margin-bottom: 6px; border: 1.5px solid var(--cf-line) }
.cf-fecha-resumen.is-en-ruta { background: #f0fcfd; border-color: rgba(0,196,212,.4) }
.cf-fecha-resumen.is-fuera { background: #fefce8; border-color: rgba(245,158,11,.4) }
.cf-fecha-resumen-tag { font-size: 11px; font-weight: 800; letter-spacing: .04em; color: var(--cf-muted); text-transform: uppercase }
.cf-fecha-resumen-dia { font-size: 16px; font-weight: 800; color: var(--cf-navy); margin-top: 2px }
.cf-fecha-resumen-precio { white-space: nowrap; font-size: 15px; font-weight: 800; color: var(--cf-navy) }
.cf-monto-ok { color: #15803d }
.cf-banner-corte { margin-top: 6px; font-size: 12px; color: var(--cf-muted); line-height: 1.45 }
.cf-resumen { background: #f8fafc; border-radius: 12px; padding: 14px 18px; margin: 8px 0 4px }
.cf-row { display: flex; justify-content: space-between; align-items: baseline; padding: 8px 0; font-size: 14px }
.cf-row + .cf-row { border-top: 1px dashed var(--cf-line) }
.cf-row span:first-child { color: var(--cf-muted); font-weight: 600 }
.cf-row-val { color: var(--cf-navy); text-align: right }
.cf-row.is-big { padding-top: 14px; margin-top: 8px; border-top: 2px solid var(--cf-navy) }
.cf-row.is-big span:first-child { font-size: 13px; color: var(--cf-navy); font-weight: 800; text-transform: uppercase; letter-spacing: .06em }
.cf-row.is-big .cf-row-val { font-size: 22px; font-weight: 900; color: var(--cf-navy) }
.cf-nav { display: flex; justify-content: space-between; gap: 10px; margin-top: 22px; padding-top: 18px; border-top: 1px solid var(--cf-line) }
.cf-btn-back { padding: 12px 20px; background: transparent; border: 0; color: var(--cf-muted); font-weight: 700; font-size: 14px; cursor: pointer; border-radius: 999px; font-family: inherit }
.cf-btn-back:hover { background: #f1f5f9; color: var(--cf-navy) }
.cf-btn-next { padding: 14px 22px; font-size: 14px; font-weight: 800; background: linear-gradient(135deg, var(--cf-blue), var(--cf-teal)); color: #fff; border: 0; border-radius: 999px; cursor: pointer; font-family: inherit; box-shadow: 0 6px 18px rgba(0,196,212,.25); transition: transform .12s, box-shadow .12s }
.cf-btn-next:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(0,196,212,.35) }
.cf-btn-next:disabled { opacity: .45; cursor: not-allowed; box-shadow: none }
.cf-confirm-pin { display: flex; align-items: center; gap: 10px; margin-top: 12px; padding: 10px 14px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer }
.cf-confirm-pin.ok { border: 1.5px solid #22c55e; background: #f0fdf4; color: #166534 }
.cf-confirm-pin.pend { border: 1.5px solid #f59e0b; background: #fefce8; color: #854d0e }
.cf-confirm-pin input { width: 16px !important; height: 16px; margin: 0 }
.cf-envgratis { margin-top: 12px; padding: 12px 14px; border-radius: 12px; background: rgba(0,168,181,.08); border: 1px solid rgba(0,168,181,.30) }
.cf-envgratis.ok { background: rgba(34,197,94,.12); border-color: rgba(34,197,94,.4); color: #34D399; font-weight: 800; font-size: 14px; text-align: center }
.cf-envgratis-txt { font-size: 13px; color: var(--cf-navy); margin-bottom: 8px }
.cf-envgratis-bar { height: 8px; border-radius: 999px; background: rgba(120,120,120,.18); overflow: hidden }
.cf-envgratis-bar span { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, var(--cf-blue), var(--cf-teal)); transition: width .3s }
.cf-btn-seguir { width: 100%; margin-top: 12px; padding: 13px 18px; font-size: 14px; font-weight: 800; background: rgba(0,168,181,.10); color: #00A8B5; border: 1.5px dashed rgba(0,168,181,.55); border-radius: 12px; cursor: pointer; font-family: inherit; transition: background .15s }
.cf-btn-seguir:hover { background: rgba(0,168,181,.18) }
.cf-item-cant button.cf-cant-trash { color: #F87171; }
.cf-item-cant button.cf-cant-trash:hover { color: #EF4444; background: rgba(239,68,68,.14) }
.cf-btn-vaciar { display: block; margin: 10px auto 0; padding: 8px 16px; font-size: 12.5px; font-weight: 700; background: transparent; color: #F87171; border: 1px solid rgba(248,113,113,.4); border-radius: 999px; cursor: pointer; font-family: inherit; transition: background .15s }
.cf-btn-vaciar:hover { background: rgba(239,68,68,.12) }
.cf-autocomplete { margin: -4px 0 12px; border: 1px solid rgba(255,255,255,.12); border-radius: 10px; overflow: hidden; background: #0F1A2E }
.cf-ac-load { padding: 12px 14px; font-size: 13px; color: rgba(248,246,241,.6) }
.cf-ac-item { display: block; width: 100%; text-align: left; padding: 11px 14px; background: transparent; border: 0; border-top: 1px solid rgba(255,255,255,.08); cursor: pointer; font-family: inherit }
.cf-ac-item:first-child { border-top: 0 }
.cf-ac-item:hover { background: rgba(0,168,181,.14) }
.cf-ac-main { display: block; font-size: 14px; font-weight: 700; color: #F8F6F1 }
.cf-ac-sub { display: block; font-size: 12px; color: rgba(248,246,241,.55); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis }
.cf-h3 { font-size: 13px; font-weight: 800; color: var(--cf-navy); margin: 20px 0 10px; text-transform: uppercase; letter-spacing: .08em }
.cf-pago { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1.5px solid var(--cf-line); border-radius: 12px; background: #fff }
.cf-pago.is-elegido { border-color: var(--cf-teal); background: #f0fcfd; box-shadow: 0 0 0 3px rgba(0,196,212,.15) }
.cf-pago > div { display: flex; flex-direction: column; gap: 2px; flex: 1 }
.cf-pago strong { font-size: 14px; color: var(--cf-navy); font-weight: 800 }
.cf-pago span { font-size: 12px; color: var(--cf-muted) }
.cf-privacy { margin-top: 16px; padding: 9px 14px; font-size: 10.5px; line-height: 1.4; color: var(--cf-muted); text-align: center; background: rgba(248,250,252,.5); border-radius: 10px; max-width: 400px; margin-left: auto; margin-right: auto; opacity: .75 }
.cf-cupon { margin-top: 12px; padding: 12px 14px; border: 1.5px dashed var(--cf-teal); border-radius: 12px; background: rgba(0,168,181,.05) }
.cf-cupon-lbl { font-size: 13px; font-weight: 800; color: var(--cf-navy); margin-bottom: 8px }
.cf-cupon-row { display: flex; gap: 8px }
.cf-cupon-row input { flex: 1; text-transform: uppercase; letter-spacing: .03em }
.cf-cupon-row input::placeholder { text-transform: none; letter-spacing: normal }
.cf-cupon-ap { flex-shrink: 0; padding: 0 20px; border: 0; border-radius: 10px; background: linear-gradient(135deg, var(--cf-blue), var(--cf-teal)) !important; color: #fff !important; font-weight: 800; font-size: 13px; cursor: pointer; font-family: inherit; box-shadow: 0 4px 12px rgba(0,196,212,.3) }
.cf-cupon-ap:disabled { opacity: .5; cursor: not-allowed; box-shadow: none }
.cf-cupon-msg { font-size: 12px; color: #fca5a5; margin-top: 8px; font-weight: 600 }
.cf-cupon-ok { display: flex; align-items: center; gap: 10px }
.cf-cupon-check { width: 24px; height: 24px; flex-shrink: 0; border-radius: 50%; background: #10b981; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 900 }
.cf-cupon-ok-txt { flex: 1; display: flex; flex-direction: column; line-height: 1.25 }
.cf-cupon-ok-txt strong { font-size: 13px; color: #6ee7b7 }
.cf-cupon-ok-txt span { font-size: 12px; color: #34d399 }
.cf-cupon-quit { flex-shrink: 0; background: transparent; border: 0; color: var(--cf-muted); font-size: 12px; font-weight: 700; text-decoration: underline; cursor: pointer; font-family: inherit }
.cf-totales-desc { color: #34d399 !important }
.cf-totales-desc span { color: #34d399 !important }
`
